import { forwardRef } from 'react';
import { Button as ChakraButton, ButtonProps as ChakraButtonProps } from '@chakra-ui/react';
import { ComponentStyleConfig } from '@chakra-ui/theme';
import { Spinner } from 'components/UI/Spinner';
import { specialFont, primaryThemeGradient, primaryThemeGradientDark } from 'constants/theme';

const solidStyles = {
  bg: primaryThemeGradient,
  _hover: {
    bg: primaryThemeGradientDark,
    _disabled: {
      bg: primaryThemeGradientDark,
    },
  },
  _focus: {
    boxShadow: 'none',
  },
  color: 'primaryFontColour.400',
  borderStyle: 'solid',
};

const ButtonStyles: ComponentStyleConfig = {
  baseStyle: {
    fontFamily: specialFont,
    fontWeight: 'normal',
    fontStretch: 'expanded',
    border: 'none',
    justifyContent: 'center',
    cursor: 'pointer',
    letterSpacing: 3,
    lineHeight: 1,
    h: 'auto',
  },
  sizes: {
    sm: {
      fontSize: 'sm',
      borderRadius: 6,
      px: 2.5,
      py: 2.5,
    },
    md: {
      fontSize: 'sm',
      borderRadius: 6,
      px: 2.5,
      py: 3.5,
    },
    lg: {
      fontSize: 'sm',
      borderRadius: 26,
      px: 36,
      py: 4,
    },
  },
  variants: {
    outline: {
      border: '2px solid',
      borderColor: 'primaryThemeColour.400',
      color: 'primaryThemeColour.400',
    },
    link: {
      textDecoration: 'underline',
      fontSize: 'md',
      lineHeight: 1,
      _hover: {
        textDecoration: 'underline',
      },
      letterSpacing: 'normal',
      _focus: {
        boxShadow: 'none',
      },
      px: 0,
      minWidth: 0,
      color: 'primaryThemeColour.400',
    },
    round: {
      borderRadius: '100%',
      ...solidStyles,
      fontSize: 'md',
    },
    solid: {
      alignSelf: 'center',
      height: 'auto',
      ...solidStyles,
    },
    unstyled: {
      _focus: {
        boxShadow: 'none',
      },
    },
  },
};

export type ButtonProps = ChakraButtonProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ size = 'md', children, variant = 'solid', ...props }: ButtonProps, ref) => (
    <ChakraButton
      colorScheme="primaryThemeColour.400"
      spinner={<Spinner size={size === 'lg' ? 'md' : 'sm'} color="white" />}
      variant={variant}
      size={size}
      ref={ref}
      {...props}
    >
      {children}
    </ChakraButton>
  )
);

export { Button, ButtonStyles };
